/**
 * MyWssForm Component
 * Main orchestrator for My WSS feature
 * Uses custom hooks to manage state, data fetching, and check-in logic
 */

import React, { useState, useCallback, useEffect } from "react";
import _ from "lodash";
import styles from "@components/MarketWorking/MyTeam/MyDealer.module.css";
import Styles from "@components/MarketWorking/MyWss/MyWss.module.css";
import { VisitDetails, MyDiary, MySR, MyDiaryList } from "@shared/components";
import CommonSnackbar from "@components/Common/CommonSnackbar";
import Loader from "@features/shared/components/Loader";
import ModalPopup from "@components/Common/ModalPopup";
import { BsInfoCircle } from "react-icons/bs";

// Feature components
import TerritorySelect from "./TerritorySelect";
import WssSelect from "./WssSelect";
import WssMap from "./WssMap";
import WssActions from "./WssActions";
import WssPowerBISection from "./WssPowerBISection";
import WssAuditPopup from "./WssAuditPopup";
import MyWssSummary from "./MyWssSummary";
import MyWssFeedbackForm from "./MyWssFeedbackForm";

// Hooks
import {
  useMyWssState,
  useLocation,
  useWssData,
  useWssCheckIn,
} from "../hooks";
import { myWssService } from "../services/wssService";

// Utils
import { validateWssForAction } from "../utils/wssValidators";
import { WSS_PLACEHOLDER } from "../utils/wssConstants";

/**
 * Props interface (for documentation)
 * @param {string} setSelectedWssCode - Parent callback to set selected WSS code
 * @param {string} setSelectedWSS - Parent callback to set selected WSS name
 */
const MyWssForm = ({ setSelectedWssCode, setSelectedWSS }) => {
  // ===== State Management =====
  const state = useMyWssState();
  const {
    // Redux state
    territoryOptions,
    userType,
    salesGroupCode,
    
    // Territory state
    selectedTerritory,
    setSelectedTerritory,
    territoryCheckIn,
    setTerritoryCheckIn,
    sh3CodeForTerritory,
    setSH3CodeForTerritory,
    
    // WSS state
    wssOption,
    setWssOption,
    selectLatLong,
    setSelectLatLong,
    wss,
    setWss,
    customerCode,
    setCustomerCode,
    accuracy,
    setAccuracy,
    mtpCheckIn,
    setMtpCheckIn,
    isWssData,
    setIsWssData,
    
    // UI state
    loading,
    setLoading,
    
    // Modals
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    isAuditPopupOpen,
    setIsAuditPopupOpen,
    isMyDiaryOpen,
    setIsMyDiaryOpen,
    isMySROpen,
    setIsMySROpen,
    isPreviousObservationOpen,
    setIsPreviousObservationOpen,
    
    // Check-in
    checkinPayLoad,
    setCheckinPayLoad,
    
    // Audit
    auditPopupData,
    setAuditPopupData,
    
    // 2-step audit flow
    recordId,
    setRecordId,
    isAuditEligible,
    setIsAuditEligible,
    setLastCheckInDate,
    isQuaterlyFlag,
    setIsQuaterlyFlag,

    // Summary
    getSummeryList,
    setGetSummeryList,
  } = state;

  // Snackbar state
  const [successOpen, setSuccessOpen] = useState(false);
  const [failedOpen, setFailedOpen] = useState(false);
  const [error, setError] = useState("");

  // ===== GPS Tracking =====
  const {
    currentLatitude,
    currentLongitude,
    currentAccuracy,
    wssLatitude,
    wssLongitude,
    wssHasCoordinates,
    distanceToWss,
    locationError,
    setWssLocation,
    openWssInMaps,
  } = useLocation();

  // ===== Helper Functions =====
  const showSuccess = useCallback((message) => {
    setSuccessOpen(true);
  }, []);

  const showError = useCallback((message) => {
    setError(message);
    setFailedOpen(true);
  }, []);

  const handleSnackbarClose = (reason) => {
    if (reason === "clickaway") return;
    setFailedOpen(false);
    setSuccessOpen(false);
  };

  // ===== Audit Eligibility =====
  // Helper function to calculate days since a given date
  const calculateDaysSince = (dateString) => {
    if (!dateString) return Infinity;
    const checkInDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - checkInDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Fetch WSS check-in history when customerCode changes
  useEffect(() => {
    if (!_.isEmpty(customerCode) && customerCode !== 0) {
      // Reset to true while loading — ensures audit popup isn't missed during async fetch
      setIsAuditEligible(true);
      let cancelled = false;
      (async () => {
        const response = await myWssService.getCheckInHistory(customerCode);
        if (cancelled) return; // Ignore stale response if WSS was switched
        if (response.success) {
          const records = response.data;
          if (records && records.length > 0) {
            const lastCheckIn = records[0]?.Check_In_Date_Time__c;
            setLastCheckInDate(lastCheckIn);
            const daysSinceLastCheckIn = calculateDaysSince(lastCheckIn);
            const daysThreshold = userType === "HO Auditor" ? 180 : 90;
            setIsAuditEligible(daysSinceLastCheckIn >= daysThreshold);
          } else {
            setLastCheckInDate(null);
            setIsAuditEligible(true);
          }
        } else {
          setLastCheckInDate(null);
          setIsAuditEligible(true);
        }
      })();
      return () => { cancelled = true; };
    }
  }, [customerCode, userType, setLastCheckInDate, setIsAuditEligible]);

  // ===== Data Fetching =====
  useWssData({
    selectedTerritory,
    selectLatLong,
    sh3CodeForTerritory,
    tourPlanDefaultData: state.tourPlanDefaultData,
    clusterCodeOption: state.clusterCodeOption,
    territoryOptions,
    userType,
    setWssOption,
    setIsWssData,
    showError,
    setTerritoryPickList: state.setTerritoryPickList,
    setCluster: state.setCluster,
    setSH3CodeForTerritory,
    setSelectedTerritory,
    setLoading,
    setWss,
    setCustomerCode,
    setMtpCheckIn,
    setAccuracy,
    setWssLocation,
    setSelectedWssCode,
    setSelectedWSS,
  });

  // ===== Check-in Logic =====
  const {
    handleCheckInClick,
    handleModalYesClick,
    handleModalNoClick,
    handleFormConfirm,
    handleFormError,
    proceedHOAuditorStampIn,
    proceedSH3AuditAndFeedback,
    handleSH3FeedbackFormSubmit,
  } = useWssCheckIn({
    wss,
    customerCode,
    distanceToWss,
    currentAccuracy,
    accuracy,
    territoryCheckIn,
    currentLatitude,
    currentLongitude,
    wssLatitude,
    wssLongitude,
    wssHasCoordinates,
    mtpCheckIn,
    userType,
    auditPopupData,
    setLoading,
    showSuccess,
    showError,
    setIsSubmitModalOpen,
    setIsAuditPopupOpen,
    setCheckinPayLoad,
    setGetSummeryList,
    selectedTerritory,
    // 2-step audit flow
    isAuditEligible,
    recordId,
    setRecordId,
    setSelectLatLong,
    setAuditPopupData,
    setIsQuaterlyFlag,
  });

  // ===== Event Handlers =====

  // Sync territoryCheckIn whenever selectedTerritory changes (covers both
  // manual selection and auto-population from MTP/tour plan defaults)
  useEffect(() => {
    if (selectedTerritory?.Id) {
      setTerritoryCheckIn(selectedTerritory.Id);
    }
  }, [selectedTerritory?.Id, setTerritoryCheckIn]);

  const handleTerritoryChange = useCallback(
    (newValue) => {
      setSelectedTerritory(newValue);
      setSelectLatLong(WSS_PLACEHOLDER);
      setWssOption([]);
    },
    [setSelectedTerritory, setSelectLatLong, setWssOption]
  );

  const handleWssChange = useCallback(
    (newValue) => {
      setSelectLatLong(newValue);
    },
    [setSelectLatLong]
  );

  const handleNavigateClick = () => {
    openWssInMaps();
  };

  const handlePreviousObservationsClick = () => {
    const validation = validateWssForAction(customerCode, "observations");
    if (!validation.isValid) {
      showError(validation.error);
      return;
    }
    setIsPreviousObservationOpen(true);
  };

  const handleMyDiaryClick = () => {
    const validation = validateWssForAction(customerCode, "diary");
    if (!validation.isValid) {
      showError(validation.error);
      return;
    }
    setIsMyDiaryOpen(true);
  };

  const handleMySRClick = () => {
    const validation = validateWssForAction(customerCode, "sr");
    if (!validation.isValid) {
      showError(validation.error);
      return;
    }
    setIsMySROpen(true);
  };

  const handleAuditSubmit = (data) => {
    setIsAuditPopupOpen(false);
    if (userType === "SH3") {
      proceedSH3AuditAndFeedback(data);
    } else if (
      (userType === "HO Auditor" || userType === "SH5") &&
      recordId
    ) {
      setAuditPopupData(data);
      proceedHOAuditorStampIn(data);
    }
  };

  const handleAutoPoulatedValue = (value) => {
    // Auto-populate from MyWssSummary
    const wssData = {
      attributes: null,
      Id: value.Id,
      CustomerName__c: value.CustomerName__c || value.WSS_Name__c,
      Customer_Code__c: value.Customer_Code__c || value.WSS_Code__c,
      WSS_latitude__c: null,
      WSS_longitude__c: null,
      Updated_WSS_latitude__c: null,
      Updated_WSS_longitude__c: null,
    };

    const territoryData = {
      attributes: null,
      Id: value.Territory_Id,
      SH2_Name__c: value.TerritoryName,
      SH2_Code__c: value.TSI_Territory_Code__c,
      Name: null,
      SH3_Code__c: null,
    };

    setSelectedTerritory(territoryData);
    setSelectLatLong(wssData);
    setIsSubmitModalOpen(true);
  };

  // ===== Render =====
  const showAuditPopup =
    (userType === "HO Auditor" || userType === "SH5" || userType === "SH3") &&
    isAuditEligible;

  return (
    <>
      <Loader loading={loading} />

      <VisitDetails name="wssPage" />

      {/* SH3 users get summary component */}
      {userType === "SH3" && (
        <MyWssSummary
          CheckIndata={handleAutoPoulatedValue}
          reloadList={getSummeryList}
        />
      )}

      <div className={styles.myDealer__outerBox}>
        <div className={styles.myDealer__heading}>My Wss</div>

        {/* Territory Select */}
        <TerritorySelect
          territoryOptions={territoryOptions}
          selectedTerritory={selectedTerritory}
          onChange={handleTerritoryChange}
        />

        {/* WSS Select */}
        <WssSelect
          wssOption={wssOption}
          selectLatLong={selectLatLong}
          isWssData={isWssData}
          onChange={handleWssChange}
        />

        {/* Google Map */}
        <WssMap
          latitude={wssLatitude}
          longitude={wssLongitude}
          locationError={locationError}
          currentLatitude={currentLatitude}
          currentLongitude={currentLongitude}
        />

        {/* Action Buttons */}
        <WssActions
          customerCode={customerCode}
          onNavigateClick={handleNavigateClick}
          onCheckInClick={handleCheckInClick}
          onPreviousObservationsClick={handlePreviousObservationsClick}
          onMyDiaryClick={handleMyDiaryClick}
          onMySRClick={handleMySRClick}
        />
      </div>

      {/* Re-capture Location Modal */}
      {isSubmitModalOpen && (
        <ModalPopup
          className="ModalPopup_Submitted"
          isModalOpen={isSubmitModalOpen}
          setIsModalOpen={setIsSubmitModalOpen}
          title="WSS Check-In Unsuccessfull"
          data="Do you want to Re-capture correct WSS location?"
          backdrop={true}
          fade={false}
          icon={
            <div className="submittedIcons draftIcons">
              <BsInfoCircle
                className="submittedSmall"
                style={{ color: "#77BFC7" }}
              />
              <BsInfoCircle
                className="submittedLarge"
                style={{ color: "#77BFC7" }}
              />
              <BsInfoCircle
                className="submittedSmall"
                style={{ color: "#77BFC7" }}
              />
            </div>
          }
          showButton={
            <>
              <Loader loading={loading} />
              <div className={Styles.myWss__saveBox}>
                <button
                  className={Styles.myWss__saveButtonNo}
                  onClick={handleModalNoClick}
                >
                  No
                </button>
                <button
                  className={Styles.myWss__saveButtonYes}
                  onClick={handleModalYesClick}
                >
                  Yes
                </button>
              </div>
            </>
          }
        />
      )}

      {/* Snackbars */}
      <CommonSnackbar
        open={failedOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        severity="error"
        message={error}
      />

      <CommonSnackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        severity="success"
        message={`Successfully checked-in for ${!_.isEmpty(wss) ? wss : "this"} wss!`}
      />

      {/* PowerBI Reports */}
      <WssPowerBISection
        salesGroupCode={salesGroupCode}
        customerCode={customerCode}
      />

      {/* Audit Popup (SH3 / SH5 / HO Auditor when audit-eligible) */}
      {showAuditPopup && (
        <WssAuditPopup
          isOpen={isAuditPopupOpen}
          setIsOpen={setIsAuditPopupOpen}
          onSubmit={handleAuditSubmit}
        />
      )}

      {/* SH3 Feedback Form */}
      {checkinPayLoad && Object.keys(checkinPayLoad).length > 0 && (
        <MyWssFeedbackForm
          record={checkinPayLoad}
          formSuccess={handleFormConfirm}
          formError={handleFormError}
          QuaterlyFlag={isQuaterlyFlag}
          isUpdateMode={userType === "SH3" && isAuditEligible && !!recordId}
          existingRecordId={recordId}
          onUpdate={handleSH3FeedbackFormSubmit}
        />
      )}

      {/* Modals */}
      {isPreviousObservationOpen && (
        <MyDiaryList
          name="wssPage"
          isOpen={isPreviousObservationOpen}
          setIsOpen={setIsPreviousObservationOpen}
        />
      )}

      {isMyDiaryOpen && (
        <MyDiary
          name="wssPage"
          selectedWssCode={customerCode}
          selectedDealerWSS={wss}
          isOpen={isMyDiaryOpen}
          setIsOpen={setIsMyDiaryOpen}
        />
      )}

      {isMySROpen && (
        <MySR
          name="WSS"
          isOpen={isMySROpen}
          setIsOpen={setIsMySROpen}
          selectedWssCode={customerCode}
          selectedDealerWSS={wss}
        />
      )}
    </>
  );
};

export default MyWssForm;
